import { faExternalLink } from "@fortawesome/pro-regular-svg-icons";

import { Link } from "@mui/material";

import { format } from "date-fns";

import { FunctionComponent } from "react";

import { Icon } from "@/components";
import { LinkedinIcon } from "@/components/LinkedinIcon/LinkedinIcon";
import { Tooltip } from "@/components/Tooltip/Tooltip";

import { useGetTrackerOrganizationSignalsQuery } from "@/pages/Private/redux/signal/signal.api";

import { classNames } from "@/utils/classNames";
import { collapsedSignalText } from "@/utils/signals/collapsedSignalText";

import { TrackerOrganization } from "../../schema/trackerOrganizations";
import { useSignalScanRequests } from "../../providers/SignalScanRequestsContext";
import { NoSignalCell } from "./NoSignalCell";
import {
	getSignalCategoryColor,
	getSignalPriorityColor,
	getSignalPriorityLabel,
} from "./SignalTimeline/signalTimelineUtils";

interface TrackerSignalCellProps {
	row: TrackerOrganization;
	/** Does the tracker's list have a signal config? Fetched once per table, never per row. */
	signalsConfigured: boolean;
}

/**
 * The Signal column of the Profile Tracker company table: the latest signal, or — when there is
 * none — the scan state (`NoSignalCell`, PREC-3616 / PREC-3240). The signal branch is checked
 * first, so a race can never draw a spinner over real data.
 */
export const TrackerSignalCell: FunctionComponent<TrackerSignalCellProps> = ({
	row,
	signalsConfigured,
}) => {
	const { requestedAt, requestSearch } = useSignalScanRequests();
	const { data: signalsData, isLoading } = useGetTrackerOrganizationSignalsQuery(
		// Signals only: the timeline endpoint also returns list appearances (PREC-2496), and one of
		// those in the single slot this column reads would blank the cell.
		{ trackerOrganizationId: row.id, limit: 1, includeListAppearances: false },
		{ skip: !row.id }
	);

	if (isLoading) {
		return <div className="text-gray-400 text-sm">Loading...</div>;
	}

	const signal = signalsData?.data?.[0]?.signal;

	if (!signal) {
		return (
			<NoSignalCell
				row={row}
				scanRequestedAt={requestedAt(row.id)}
				signalsConfigured={signalsConfigured}
				onRunSearch={() => requestSearch({ ids: [row.id] })}
			/>
		);
	}

	// PREC-5037/5038: the collapsed cell says what the signal is about (its summary), not only who
	// posted it; the tooltip keeps title, reasoning and the full summary.
	const collapsed = collapsedSignalText({ title: signal.title, summary: signal.summary });

	const tooltipContent = (
		<div className="flex flex-col gap-2 max-w-[350px] p-1">
			{/* Badges row */}
			<div className="flex items-center gap-2 flex-wrap">
				{signal.priority && (
					<span
						className={classNames(
							"px-2 py-0.5 text-xs font-semibold rounded border",
							getSignalPriorityColor(signal.priority)
						)}
					>
						{getSignalPriorityLabel(signal.priority)}
					</span>
				)}
				{signal.signalCategory && (
					<span
						className={classNames(
							"px-2 py-0.5 text-xs font-semibold rounded border",
							getSignalCategoryColor(signal.signalCategory)
						)}
					>
						{signal.signalCategory}
					</span>
				)}
			</div>

			{/* Title */}
			{signal.title && (
				<h4 className="text-sm font-semibold text-text leading-5">{signal.title}</h4>
			)}

			{/* Reasoning */}
			{signal.reasoning && (
				<div className="p-2 bg-blue-50 border-l-2 border-blue-400 rounded">
					<h5 className="text-xs font-semibold text-gray-700 mb-1">Why is this relevant</h5>
					<p className="text-xs text-gray-600">{signal.reasoning}</p>
				</div>
			)}

			{/* Summary */}
			{signal.summary && <p className="text-sm text-gray-600">{signal.summary}</p>}

			{/* Timestamps */}
			<div className="flex flex-col gap-1 text-xs text-gray-500 border-t pt-2 mt-1">
				{signal.publishedDate && (
					<span>Published: {format(new Date(signal.publishedDate), "d MMM yyyy, HH:mm")}</span>
				)}
				<span>Collected: {format(new Date(signal.createdAt), "d MMM yyyy, HH:mm")}</span>
			</div>
		</div>
	);

	return (
		<div className="flex items-center gap-2 w-[200px] max-w-[200px]">
			{signal.sourceUrl && (
				<Link
					className="flex-shrink-0"
					href={signal.sourceUrl}
					target="_blank"
					onClick={(e) => e.stopPropagation()}
				>
					{signal.sourceUrl.includes("linkedin.com") ? (
						<LinkedinIcon />
					) : (
						<Icon className="w-4 h-4 text-gray-500 hover:text-blue-action" icon={faExternalLink} />
					)}
				</Link>
			)}
			<Tooltip content={tooltipContent}>
				<span className="text-sm overflow-hidden text-ellipsis whitespace-nowrap block max-w-[170px] cursor-help">
					{collapsed.text ?? <span className="text-gray-400 italic">No title</span>}
				</span>
			</Tooltip>
		</div>
	);
};
